import axios from "axios"

const TUS_VERSION = "1.0.0"
const TUS_CHUNK_SIZE = 10 * 1024 * 1024
const TUS_RETRY_DELAYS = [0, 800, 1800]

export const MAX_VIDEO_SIZE = 5 * 1024 * 1024 * 1024

export function getMediaSizeError(file) {
  if (!file) return ""
  const mimeType = String(file.type || "").trim().toLowerCase()
  const isVideo = mimeType && mimeType !== "application/octet-stream"
    ? mimeType.startsWith("video/")
    : /\.(mp4|m4v|mov|webm|avi|3gp)$/i.test(file.name || "")
  const maxSize = isVideo ? MAX_VIDEO_SIZE : 250 * 1024 * 1024
  if (file.size > maxSize) return isVideo ? "Video max. 5 GB" : "Datei max. 250 MB"
  return ""
}

function createUploadError(message, status = null) {
  const error = new Error(message)
  if (status) error.status = status
  return error
}

function isCanceled(signal) {
  return Boolean(signal && signal.aborted)
}

function wait(delay, signal) {
  if (!delay) return Promise.resolve()
  return new Promise((resolve, reject) => {
    let abort = null
    const timeoutId = setTimeout(() => {
      if (signal && abort) signal.removeEventListener("abort", abort)
      resolve()
    }, delay)
    if (!signal) return
    abort = () => {
      clearTimeout(timeoutId)
      reject(createUploadError("Upload abgebrochen."))
    }
    if (signal.aborted) abort()
    else signal.addEventListener("abort", abort, { once: true })
  })
}

function sendTusRequest({ method, url, body = null, offset = null, signal = null, onProgress = null }) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open(method, url, true)
    request.setRequestHeader("Tus-Resumable", TUS_VERSION)
    if (method === "PATCH") {
      request.setRequestHeader("Content-Type", "application/offset+octet-stream")
      request.setRequestHeader("Upload-Offset", String(offset))
    }

    const abort = () => request.abort()
    if (signal) {
      if (signal.aborted) {
        reject(createUploadError("Upload abgebrochen."))
        return
      }
      signal.addEventListener("abort", abort, { once: true })
    }

    const cleanup = () => {
      if (signal) signal.removeEventListener("abort", abort)
    }
    request.onerror = () => {
      cleanup()
      reject(createUploadError("Die Verbindung zu Cloudflare Stream wurde unterbrochen."))
    }
    request.onabort = () => {
      cleanup()
      reject(createUploadError("Upload abgebrochen."))
    }
    request.onload = () => {
      cleanup()
      if (request.status < 200 || request.status >= 300) {
        reject(createUploadError("Cloudflare Stream hat einen Upload-Teil abgelehnt.", request.status))
        return
      }
      const offsetHeader = request.getResponseHeader("Upload-Offset")
      const nextOffset = offsetHeader === null ? Number.NaN : Number(offsetHeader)
      const fallbackOffset = method === "PATCH" && body && Number.isFinite(offset) ? offset + body.size : null
      resolve(Number.isFinite(nextOffset) ? nextOffset : fallbackOffset)
    }
    if (request.upload && onProgress) {
      request.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded, event.total)
      }
    }
    request.send(body)
  })
}

async function getTusOffset(uploadUrl, signal) {
  const offset = await sendTusRequest({ method: "HEAD", url: uploadUrl, signal })
  if (!Number.isFinite(offset) || offset < 0) throw createUploadError("Cloudflare Stream hat keinen gültigen Upload-Stand geliefert.")
  return offset
}

async function uploadTus(file, session, { signal = null, onProgress = null } = {}) {
  if (!session.uploadUrl) throw createUploadError("Die Cloudflare Stream Upload-URL fehlt.")
  let offset = await getTusOffset(session.uploadUrl, signal)
  if (offset > file.size) throw createUploadError("Der gespeicherte Upload-Stand ist ungültig.")
  if (onProgress) onProgress(offset, file.size)

  while (offset < file.size) {
    if (isCanceled(signal)) throw createUploadError("Upload abgebrochen.")
    const end = Math.min(offset + TUS_CHUNK_SIZE, file.size)
    const chunk = file.slice(offset, end)
    let uploadedOffset = null
    let lastError = null

    for (const delay of TUS_RETRY_DELAYS) {
      try {
        await wait(delay, signal)
        uploadedOffset = await sendTusRequest({
          method: "PATCH",
          url: session.uploadUrl,
          body: chunk,
          offset,
          signal,
          onProgress: (loaded) => {
            if (onProgress) onProgress(Math.min(offset + loaded, file.size), file.size)
          },
        })
        if (!Number.isFinite(uploadedOffset) || uploadedOffset <= offset || uploadedOffset > end) {
          throw createUploadError("Cloudflare Stream hat einen ungültigen Upload-Stand geliefert.")
        }
        break
      } catch (error) {
        if (isCanceled(signal)) throw error
        lastError = error
        try {
          const remoteOffset = await getTusOffset(session.uploadUrl, signal)
          if (remoteOffset > file.size) throw createUploadError("Der gespeicherte Upload-Stand ist ungültig.")
          if (remoteOffset > offset) {
            uploadedOffset = remoteOffset
            break
          }
        } catch (_) {
          // Der nächste Retry entscheidet, ob die Verbindung wieder verfügbar ist.
        }
      }
    }

    if (uploadedOffset === null) throw lastError || createUploadError("Der Video-Upload konnte nicht fortgesetzt werden.")
    offset = uploadedOffset
    if (onProgress) onProgress(offset, file.size)
  }
}

async function uploadR2(file, session, { signal = null, onProgress = null } = {}) {
  if (!session.uploadUrl) throw createUploadError("Die Cloudflare R2 Upload-URL fehlt.")
  await axios.put(session.uploadUrl, file, {
    headers: session.headers || { "Content-Type": file.type },
    signal,
    onUploadProgress: (event) => {
      if (onProgress) onProgress(event.loaded, event.total || file.size)
    },
  })
}

export async function uploadCloudflareMedia(file, session, options = {}) {
  if (!file || !session) throw createUploadError("Datei oder Upload-Session fehlt.")
  if (session.provider === "cloudflare_stream") {
    await uploadTus(file, session, options)
    return session
  }
  if (session.provider === "cloudflare_r2") {
    await uploadR2(file, session, options)
    return session
  }
  throw createUploadError("Dieser Cloudflare Upload-Typ wird nicht unterstützt.")
}
